/**
 * « À faire » de l'Accueil élève — ce qu'il doit voir en ouvrant l'app, dans
 * l'ordre où il doit le faire. PUR, testé (`today.test.ts`).
 *
 * Ordre : devoirs en retard → devoirs pour aujourd'hui / ≤ 2 jours → exercice
 * en cours (reprise) → autres devoirs → diagnostic à faire / à reprendre.
 * Un devoir déjà commencé n'apparaît qu'une fois (en devoir, avec « Reprendre »).
 */

import type { InProgressExercise } from '@/hooks/use-in-progress-exercises';
import type { DiagnosticState } from '@/lib/skill-diagnostic';
import { dueLabel, dueState, type DueState, type Homework } from './homework';

export type TodayItem =
  | {
      kind: 'homework';
      key: string;
      homework: Homework;
      due: DueState;
      dueText: string | null;
      /** Tentative en cours sur cet exercice (bouton « Reprendre »). */
      resumeProgress: number | null;
    }
  | { kind: 'resume'; key: string; exercise: InProgressExercise; progress: number | null }
  | { kind: 'diagnostic'; key: string; answered: number; total: number; started: boolean };

/** Nombre de lignes affichées avant « Voir les N autres ». */
export const TODAY_VISIBLE = 4;

function progressOf(ex: Pick<InProgressExercise, 'stepsCompleted' | 'totalSteps'>) {
  if (ex.totalSteps <= 0) return null;
  return Math.min(100, Math.round((ex.stepsCompleted / ex.totalSteps) * 100));
}

export function buildTodayItems({
  homework,
  inProgress,
  diagnostic,
  now = new Date(),
  maxResume = 2,
}: {
  homework: readonly Homework[];
  inProgress: readonly InProgressExercise[];
  diagnostic: DiagnosticState | null | undefined;
  now?: Date;
  maxResume?: number;
}): TodayItem[] {
  const progressByExercise = new Map(inProgress.map((ex) => [ex.exerciseId, ex] as const));
  const homeworkExerciseIds = new Set(homework.map((h) => h.exerciseId));

  const hwItems: TodayItem[] = homework.map((h) => {
    const running = progressByExercise.get(h.exerciseId);
    return {
      kind: 'homework',
      key: `hw-${h.id}`,
      homework: h,
      due: dueState(h.dueAt, now),
      dueText: dueLabel(h.dueAt, now),
      resumeProgress: running ? (progressOf(running) ?? 0) : null,
    };
  });
  const urgent = hwItems.filter(
    (i) => i.kind === 'homework' && (i.due === 'late' || i.due === 'today' || i.due === 'soon'),
  );
  const others = hwItems.filter((i) => !urgent.includes(i));

  const resume: TodayItem[] = inProgress
    .filter((ex) => !homeworkExerciseIds.has(ex.exerciseId))
    .slice(0, maxResume)
    .map((ex) => ({
      kind: 'resume',
      key: `run-${ex.attemptId}`,
      exercise: ex,
      progress: progressOf(ex),
    }));

  const items: TodayItem[] = [...urgent, ...resume, ...others];

  const current = diagnostic?.available ? diagnostic.current : null;
  if (current && (current.status === 'pending' || current.status === 'in_progress')) {
    items.push({
      kind: 'diagnostic',
      key: `diag-${current.id}`,
      answered: current.answered_count,
      total: current.question_count,
      started: current.status === 'in_progress',
    });
  }
  return items;
}

/** Phrase-bilan au-dessus de la liste (vide → rien à faire d'urgent). */
export function todaySummary(items: readonly TodayItem[]): string {
  const hw = items.filter((i) => i.kind === 'homework');
  const late = hw.filter((i) => i.kind === 'homework' && i.due === 'late').length;
  if (hw.length === 0) {
    if (items.some((i) => i.kind === 'resume')) return 'Reprends là où tu t’étais arrêté·e.';
    if (items.length > 0) return 'Une chose à faire pour bien démarrer.';
    return 'Rien d’urgent : profites-en pour t’entraîner.';
  }
  const base = hw.length === 1 ? '1 devoir à faire' : `${hw.length} devoirs à faire`;
  return late > 0 ? `${base}, dont ${late} en retard.` : `${base}.`;
}
